/**
 * Tenant download of platform KPIs by an admin tag.
 * Copies missing definitions (including tags). Existing names are not duplicated.
 */

import React, { useEffect, useState } from 'react';
import api from '@/services/api';
import { Button } from '@/components/ui/Button';
import { useLanguage } from '@/state/language/LanguageProvider';
import toast from 'react-hot-toast';

interface TagPack {
  id: string;
  name: string;
  count: number;
}

/** Cached "KPI pack applied." has no placeholders — always show the counts. */
function downloadResultMessage(added: number, existed: number, translated: string): string {
  const fallback = `${added} new KPIs copied. ${existed} already existed.`;
  if (!/\d/.test(translated)) return fallback;
  return translated;
}

export const KpiPackDownload: React.FC<{ onApplied?: () => void }> = ({ onApplied }) => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [packs, setPacks] = useState<TagPack[]>([]);
  const [selected, setSelected] = useState('');
  const [busy, setBusy] = useState(false);
  const [resultMessage, setResultMessage] = useState('');

  useEffect(() => {
    if (!open) return;
    api.get('/kpis/packs').then((response) => {
      const rows = (response.data?.data || []) as TagPack[];
      setPacks(rows);
      if (rows[0] && !selected) setSelected(rows[0].id);
    }).catch(() => toast.error(t('kpi.packLoadFailed')));
  }, [open, t]);

  const apply = async () => {
    if (!selected) return;
    try {
      setBusy(true);
      const response = await api.post('/kpis/packs/by-tag/apply', { tag: selected });
      const added = Number(response.data?.data?.added) || 0;
      const existed = Number(response.data?.data?.existed) || 0;
      const message = downloadResultMessage(
        added,
        existed,
        t('kpi.packApplied', { added, existed })
      );
      setResultMessage(message);
      toast.success(message, { duration: 8000 });
      onApplied?.();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t('kpi.packApplyFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-3">
      <Button variant="outline" onClick={() => setOpen((value) => !value)}>
        {t('kpi.downloadPack')}
      </Button>
      {open && (
        <div className="mt-2 p-3 border border-[var(--color-border)] rounded bg-[var(--color-surface)] space-y-2">
          <label className="block text-sm text-[var(--color-secondary-text)]" htmlFor="kpi-pack">
            {t('kpi.choosePack')}
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <select
              id="kpi-pack"
              className="border border-[var(--color-border)] rounded px-2 py-1 text-sm max-w-md"
              value={selected}
              onChange={(event) => setSelected(event.target.value)}
            >
              {packs.map((pack) => (
                <option key={pack.id} value={pack.id}>
                  {pack.name} ({pack.count})
                </option>
              ))}
            </select>
            <Button variant="primary" onClick={apply} disabled={busy || !selected}>
              {t('kpi.applyPack')}
            </Button>
          </div>
          {resultMessage && (
            <p className="text-sm text-[var(--color-surface-text)]">{resultMessage}</p>
          )}
          {packs.length === 0 && (
            <p className="text-sm text-[var(--color-secondary-text)]">{t('kpi.noPacks')}</p>
          )}
        </div>
      )}
    </div>
  );
};
